import type { MutableRefObject } from 'react'
import { toast } from 'sonner'
import type { TextChain } from '@/api/types/stowed-element'
import type { TemplateLayer, TemplateLayerHints, TemplateLayerRef } from '@/api/types/template'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import type { UndoAction } from '@/lib/history/types/undo-action'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import {
  type TemplatePlacement,
  buildTemplatePlacement,
} from '@/lib/templates/build-template-placement'
import { mergeTemplateTextChains } from '@/lib/templates/merge-template-text-chains'
import { resolveTemplateLayerRef } from '@/lib/templates/resolve-template-layer-ref'
import { recordTemplatePlacement } from '@/lib/templates/template-placement-session'
import { topFractionalIndex } from '@/lib/templates/top-fractional-index'
import type { TemplateListItem } from '@/lib/templates/types/template-list-item'
import { logger } from '@/utils/logger'

export type PlaceTemplateDeps = {
  shapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  spreadIdRef: MutableRefObject<string>
  unifiedLayerIdsRef: MutableRefObject<string[]>
  setUnifiedLayerIds: (ids: string[]) => void
  spreadProjection: DocumentProjection
  isGroupShape: (shape: ShapeData) => boolean
  textChainsRef: MutableRefObject<TextChain[]>
  setTextChains: (chains: TextChain[]) => void
  pushAction: (action: UndoAction) => void
  /** Saves the chain list, riding on the named placed frame (see `persistChainRider`). */
  persistChainRider: (chains: TextChain[], riderFrameId: string, historyTxnId: string) => void
  /** Reserves and returns the next free text-frame uid, i.e. one above the highest in use. */
  mintTextFrameUid: () => number
  /** The highest uid handed out so far, read without minting one. */
  getTextFrameUidHighWater: () => number
  /** Marks every uid up to this one as taken, past the ones a template arrived with. */
  reserveTextFrameUidsThrough: (uid: number) => void
  /** Gives placed shapes that carry only an image id (a template's ClikArt and photos) their
   *  preview URL, so they draw the moment they are added. Best effort; never rejects. */
  resolveImagePreviews: (shapes: ShapeData[]) => Promise<ShapeData[]>
  /** Loads the template's layer for this placement; the response depends on the hints. */
  loadTemplateLayer: (ref: TemplateLayerRef, hints: TemplateLayerHints) => Promise<TemplateLayer>
}

/**
 * A template dropped on the spread (US-13). Legacy copies a template's elements onto the spread
 * unchanged, so this does the same: ask the backend for the template's layer (new ids, indexes
 * above the spread's top element, uids above its highest), and add every element at the geometry
 * the template stores. The drop point is deliberately ignored.
 *
 * The whole placement is one undo step: every add, and the chain change below, share one
 * `historyTxnId`, like paste. `addShapeData` reaches `handleShapeCreated`, which records each add
 * and enqueues it, so nothing is enqueued or pushed for the elements here.
 *
 * A template's linked text is added to the spread's own chains rather than replacing them: the
 * write that carries chains replaces the whole list, so the merged list is what is sent.
 *
 * Every drop fetches fresh. The response is only valid for the hints it was asked with (ids and
 * uids would repeat on a second placement), so there is nothing to cache.
 *
 * Nothing here rejects: a failure at any step is logged and shown as a toast.
 */
export async function placeTemplate(
  template: TemplateListItem,
  deps: PlaceTemplateDeps
): Promise<void> {
  const spreadId = deps.spreadIdRef.current
  let loaded: LoadedTemplate | null

  try {
    loaded = await loadTemplateForCurrentHints(template, deps)
  } catch (error) {
    logger.warn('Failed to load template', error)
    toast.error(`Couldn't place ${template.templateName}. Please try again.`)
    return
  }

  // The user may have moved to another spread while the template loaded.
  if (deps.spreadIdRef.current !== spreadId) return

  if (!loaded) {
    logger.warn(`Template ${template.id}: the spread kept changing while it loaded`)
    toast.error(`Couldn't place ${template.templateName}. Please try again.`)
    return
  }

  try {
    addTemplateToSpread(template, loaded, spreadId, deps)
  } catch (error) {
    logger.warn('Failed to add template elements', error)
    toast.error(`Couldn't place ${template.templateName}. Please try again.`)
  }
}

/** A template's layer, with its elements already built into shapes and ready to add. */
type LoadedTemplate = { layer: TemplateLayer; placement: TemplatePlacement }

/** How many times a layer is asked for again because the spread moved on while it loaded. */
const MAX_LAYER_LOAD_ATTEMPTS = 3

/**
 * Loads the template's layer and builds its shapes, asking again when the spread changed while that
 * was under way.
 *
 * Its uids and indexes are built above the hints sent (`uidSeed`, `after`), but other edits keep
 * running during the request: a new text frame, a paste or a link takes the next uid, and a new
 * shape takes the next index. If either has moved past what was sent, the response could tie with
 * those, so the hints are read again and the layer is requested again. Nothing has been added yet,
 * so a retry costs one request. Returns null when every attempt was overtaken.
 *
 * The shapes' preview URLs are looked up inside the same window, before the check, because that
 * lookup is a second wait during which the spread can change just the same.
 */
async function loadTemplateForCurrentHints(
  template: TemplateListItem,
  {
    shapeManagerRef,
    spreadIdRef,
    spreadProjection,
    mintTextFrameUid,
    getTextFrameUidHighWater,
    loadTemplateLayer,
    resolveImagePreviews,
  }: PlaceTemplateDeps
): Promise<LoadedTemplate | null> {
  const spreadId = spreadIdRef.current
  const readTopIndex = () => topFractionalIndex(shapeManagerRef.current?.getShapes() ?? [])

  for (let attempt = 0; attempt < MAX_LAYER_LOAD_ATTEMPTS; attempt++) {
    const uidSeed = mintTextFrameUid()
    const after = readTopIndex()
    const layer = await loadTemplateLayer(resolveTemplateLayerRef(template), { uidSeed, after })

    // On another spread there is nothing to place; the caller sees that and ends quietly.
    if (spreadIdRef.current !== spreadId) return null

    const built = buildTemplatePlacement({ elements: layer.elements, projection: spreadProjection })
    const placement = { ...built, shapes: await resolveImagePreviews(built.shapes) }

    if (spreadIdRef.current !== spreadId) return null

    if (getTextFrameUidHighWater() === uidSeed && readTopIndex() === after) {
      return { layer, placement }
    }
  }

  return null
}

/** Adds a loaded template's shapes to the spread as one undo step. */
function addTemplateToSpread(
  template: TemplateListItem,
  { layer, placement }: LoadedTemplate,
  spreadId: string,
  {
    shapeManagerRef,
    unifiedLayerIdsRef,
    setUnifiedLayerIds,
    isGroupShape,
    textChainsRef,
    setTextChains,
    pushAction,
    persistChainRider,
    reserveTextFrameUidsThrough,
  }: PlaceTemplateDeps
): void {
  const mgr = shapeManagerRef.current

  if (!mgr) return

  if (placement.shapes.length === 0) {
    toast.error(`${template.templateName} has nothing to place.`)
    return
  }

  if (placement.skippedCount > 0) {
    logger.warn(`Template ${template.id}: ${placement.skippedCount} element(s) could not be placed`)
  }

  const historyTxnId = `template:${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

  for (const shape of placement.shapes) {
    mgr.addShapeData(shape, {
      selectAfterCreate: false,
      meta: { historyTxnId, ...(isGroupShape(shape) ? { isGroupCreation: true } : {}) },
    })
  }

  // Each add appended its id to the layer order as it was created, which puts a group after its
  // children. The fractional indexes already follow the template's stacking, so only the live
  // order needs correcting to match.
  const placedIds = new Set(placement.stackingOrder)
  const stackedOrder = [
    ...unifiedLayerIdsRef.current.filter((id) => !placedIds.has(id)),
    ...placement.stackingOrder,
  ]

  unifiedLayerIdsRef.current = stackedOrder
  setUnifiedLayerIds(stackedOrder)

  // The template's uids are now in use; the next new text frame must not reuse one.
  reserveTextFrameUidsThrough(placement.maxUid)

  addTemplateTextChains({
    templateChains: layer.textChains,
    placedShapes: placement.shapes,
    historyTxnId,
    textChainsRef,
    setTextChains,
    pushAction,
    persistChainRider,
  })

  recordTemplatePlacement(spreadId, template.id, placement.stackingOrder)
}

/** Adds the template's chains to the spread's, as part of the placement's one undo step. */
function addTemplateTextChains({
  templateChains,
  placedShapes,
  historyTxnId,
  textChainsRef,
  setTextChains,
  pushAction,
  persistChainRider,
}: Pick<
  PlaceTemplateDeps,
  'textChainsRef' | 'setTextChains' | 'pushAction' | 'persistChainRider'
> & {
  templateChains: TextChain[]
  placedShapes: ShapeData[]
  historyTxnId: string
}): void {
  if (templateChains.length === 0) return

  const chainsBefore = textChainsRef.current
  const chainsAfter = mergeTemplateTextChains(chainsBefore, templateChains)

  if (chainsAfter.length === chainsBefore.length) return

  // Any frame of a chain we added can carry the chain list in its write.
  const riderUid = chainsAfter[chainsBefore.length].textBox[0]?.uid
  const rider = placedShapes.find((shape) => shape.uid != null && String(shape.uid) === riderUid)

  if (!rider) {
    logger.warn('Template text chain names no placed frame; its links were not added')
    return
  }

  textChainsRef.current = chainsAfter
  setTextChains(chainsAfter)
  pushAction({
    type: 'textLinkChange',
    chainsBefore,
    chainsAfter,
    riderTextBoxId: rider.id,
    historyTxnId,
  })
  persistChainRider(chainsAfter, rider.id, historyTxnId)
}
